import type { PostMediaItem, PostSearchItem } from '@ps/api/types';

/**
 * Mirror of legacy
 * `flows/bio/wall/functions/postSearchItemToPost.ts:85-105` —
 * for a restricted post, blank out playable URLs for media
 * past the `unrestrictedLeadingMediaCount` window.
 *
 * Why this matters:
 *
 *   - the lock overlay (`PostLockedView`) sits on top of the
 *     paid media, but the underlying `<video src=…>` /
 *     `<img src=…>` would still trigger a network request to
 *     the gated CDN URL — leaks to the network tab.
 *
 *   - if `<video>` somehow got mounted with the real `src`,
 *     it would buffer and decode paywalled content before the
 *     overlay loaded. Stripping the URL here keeps the gated
 *     content off the wire on the client.
 *
 *   - `processedUrl` and `thumbnailUrl` are also stripped —
 *     even the poster image is paywalled.
 *
 * Returns the same `medias` reference unchanged when nothing
 * needs blanking, so React reconciles cheaply.
 */
export function restrictedMedias(
  post: Pick<
    PostSearchItem,
    'medias' | 'restricted' | 'unrestrictedLeadingMediaCount'
  >,
): PostMediaItem[] {
  const medias = post.medias;
  if (!medias || medias.length === 0) return [];
  // Unrestricted post → nothing to blank.
  if (post.restricted !== true) return medias;

  const cap = post.unrestrictedLeadingMediaCount ?? 0;
  // Every slide is gated → nothing to expose; blank all.
  // Leading-count > slides → all slides are visible; nothing
  // to blank.
  if (cap >= medias.length) return medias;

  // Build a new array; preserve the prefix references so the
  // gallery's keyed slides don't tear down.
  return medias.map((media, index) => {
    if (index < cap) return media;
    // Past the unrestricted window — strip the playable
    // URLs. Keep `id`, `type`, `contentType`, `caption` so
    // the gallery can still render a sized blank cell with
    // the lock overlay over it.
    return {
      ...media,
      url: '',
      processedUrl: undefined,
      thumbnailUrl: undefined,
      thumbnailVideoUrl: undefined,
    };
  });
}
